import {
  HttpMethod,
  ApiMethod,
  API_METHODS,
} from './methods';

interface RequestParams {
  body?: Record<string, unknown>;
  qs?: Record<string, string>;
  requestParams?: Record<string, unknown>;
  [key: string]: unknown;
}

interface RequestConfig {
  url: URL;
  method: HttpMethod;
  headers: Record<string, string>;
  data?: string;
  [key: string]: unknown;
}

interface MockRequest {
  url: URL;
  endpointUrl: string;
  params: RequestParams;
}

type MockMethod = (request: MockRequest) => Promise<unknown>;

interface ApiFactoryConfig {
  makeRequest: (config: RequestConfig) => Promise<unknown>;
}

interface ApiInstanceBase {
  getApiUrl: () => string;
  setApiUrl: (url: string) => string;
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  setMockMethods: (mockObject: Record<string, MockMethod>, merge?: boolean) => void;
  clearMockMethods: () => Record<string, MockMethod>;
  setAdditionalParams: (params: RequestParams, merge?: boolean) => void;
  clearAdditionalParams: () => RequestParams;
}

interface ApiMethods {
  [methodName: string]: (params?: RequestParams) => Promise<unknown>;
}

type ApiInstance = ApiInstanceBase & ApiMethods;

const STRING_TEMPLATE_REGEX = /\{([0-9a-zA-Z_]+)\}/g;

const formatString = (
  template: string,
  args: Record<string, string> = {},
): string => template.replace(
  STRING_TEMPLATE_REGEX, (match, key: string, index: number) => {
    if (template[index - 1] === '{' && template[index + match.length] === '}') {
      return key;
    }

    return args[key] || '';
  },
);

const buildQueryUrl = (
  apiUrl: string,
  endpointUrl: string,
  params: RequestParams,
): URL => {
  if (!apiUrl) {
    throw new Error('API URL is not configured');
  }

  const url = new URL(`${apiUrl}${formatString(endpointUrl, params as Record<string, string>)}`);

  if (params.qs) {
    url.search = new URLSearchParams(params.qs).toString();
  }

  return url;
};

const apiFactory = ({ makeRequest }: ApiFactoryConfig): ApiInstance => {
  let apiUrl = '';
  let mockMethods: Record<string, MockMethod> = {};
  let accessToken: string | null = null;
  let additionalParams: RequestParams = {};

  const performFetch = async ({
    endpoint,
    method,
    methodName,
    params,
  }: ApiMethod & {
    params: RequestParams;
  }): Promise<unknown> => {
    const url = buildQueryUrl(apiUrl, endpoint, params);

    if (mockMethods[methodName]) {
      return mockMethods[methodName]({
        url,
        endpointUrl: endpoint,
        params,
      });
    }

    const requestConfig: RequestConfig = {
      url,
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(accessToken && {
          Authorization: `Bearer ${accessToken}`,
        }),
      },
      ...(params.body && {
        data: JSON.stringify(params.body),
      }),
      ...params.requestParams,
    };

    return makeRequest(requestConfig);
  };

  const baseInstance: ApiInstanceBase = {
    getApiUrl: () => apiUrl,
    setApiUrl: (url: string) => {
      apiUrl = url;
      return apiUrl;
    },
    getAccessToken: () => accessToken,
    setAccessToken: (token: string | null) => {
      accessToken = token;
    },
    setMockMethods: (mockObject: Record<string, MockMethod>, merge = true) => {
      mockMethods = merge ? { ...mockMethods, ...mockObject } : mockObject;
    },
    clearMockMethods: () => {
      mockMethods = {};
      return mockMethods;
    },
    setAdditionalParams: (params: RequestParams, merge = true) => {
      additionalParams = merge ? { ...additionalParams, ...params } : params;
    },
    clearAdditionalParams: () => {
      additionalParams = {};
      return additionalParams;
    },
  };

  const apiMethods: ApiMethods = {};
  API_METHODS.forEach(({ methodName, endpoint, method }) => {
    apiMethods[methodName] = (params: RequestParams = {}) => performFetch({
      method,
      methodName,
      endpoint,
      params: {
        ...additionalParams,
        ...params,
      },
    });
  });

  return {
    ...baseInstance,
    ...apiMethods,
  } as ApiInstance;
};

export default apiFactory;
